const HEADER_MENU_LABEL_BY_ID = {
    platform: 'Platform',
    products: 'Products',
    solutions: 'Solutions',
    pricing: 'Pricing',
    partners: 'Partners',
    support: 'Support & Resources',
    'for-msps': 'For MSPs',
    company: 'Why Acronis',
    trials: 'Trials',
    'buy-online': 'Buy online',
    contacts: 'Contact us',
    login: 'Login',
    'ai-native': 'AI-Native',
};

export const HEADER_MENU_LOCATION = 'Header Menu';

const labelFromId = (id = '') => String(id)
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const headerMenuEventLabel = ({ id, type, eventLabel } = {}) => {
    if (eventLabel) return eventLabel;
    if (id === 'company' && type === 'ai-gradient') return 'AI-Native';

    return HEADER_MENU_LABEL_BY_ID[id] || labelFromId(id) || '(not set)';
};

export const headerMenuDataLayerEvent = (item, position, location = 's-main-header') => ({
    event: 'Acronis',
    eventCategory: 'Interactions',
    eventAction: 'Header Menu',
    eventLabel: headerMenuEventLabel(item),
    eventContext: 'click',
    eventLocation: location,
    eventButton: String(position),
});

export const headerSubMenuDataLayerEvent = ({ menuId, submenuLabel, position, location = '' } = {}) => ({
    event: 'Acronis',
    eventCategory: 'Interactions',
    eventAction: 'Header Sub Menu',
    eventLabel: [headerMenuEventLabel({ id: menuId }), submenuLabel].filter(Boolean).join(' - '),
    eventContext: 'click',
    eventLocation: location,
    eventButton: position === '' || position == null ? '' : String(position),
});

export const headerMenuLinkEvent = (item, position, location = 's-main-header') => {
    const event = headerMenuDataLayerEvent(item, position, location);

    return {
        event: event.event,
        category: event.eventCategory,
        action: event.eventAction,
        label: event.eventLabel,
        context: event.eventContext,
        location: event.eventLocation,
        button: event.eventButton,
    };
};

const MEGA_MENU_ACTION_BY_ID = {
    platform: 'Menu Platform',
    solutions: 'Menu Solutions',
    pricing: 'Menu Pricing',
    partners: 'Menu Partners',
    support: 'Menu Support & Resources',
};

const MENU_UTM_WEBTEAM_BY_KEYWORD = {
    provider: 'mp-sp',
    business: 'mp-business',
    home: 'mp-home',
};

const PLATFORM_MENU_UTM_WEBTEAM_BY_KEYWORD = {
    'data protection': 'mp-data-protection',
    cybersecurity: 'mp-cybersecurity',
    operations: 'mp-operations',
    infrastructure: 'mp-infrastructure',
    platform: 'mp-platform',
};

export const isTrackedMegaMenu = (menuId) => Boolean(MEGA_MENU_ACTION_BY_ID[menuId]);

export const menuUtmWebteam = (eventLocation = '', menuId = '') => {
    const normalizedLocation = String(eventLocation).toLowerCase();
    if (menuId === 'platform') {
        // Mobile locations include the parent Platform label: match the category
        // first so it takes precedence over the common parent.
        const platformLocation = normalizedLocation.replace(/[-_]+/g, ' ');
        const platformKeyword = Object.keys(PLATFORM_MENU_UTM_WEBTEAM_BY_KEYWORD)
            .find((item) => platformLocation.includes(item));
        if (platformKeyword) return PLATFORM_MENU_UTM_WEBTEAM_BY_KEYWORD[platformKeyword];
    }

    const keyword = Object.keys(MENU_UTM_WEBTEAM_BY_KEYWORD)
        .find((item) => normalizedLocation.includes(item));

    return MENU_UTM_WEBTEAM_BY_KEYWORD[keyword] || null;
};

export const megaMenuEventLocation = ({ menuId, submenuLocation } = {}) => [
    HEADER_MENU_LOCATION,
    menuId ? headerMenuEventLabel({ id: menuId }) : '',
    submenuLocation,
].filter(Boolean).join(' - ');

/**
 * GTM expects only the destination path: locale, domain, query and fragment are omitted.
 * The original trailing slash is preserved by URL.pathname.
 */
export const megaMenuEventLabel = (destination = '') => {
    if (typeof destination !== 'string' || !destination.trim()) return '';
    if (/^[?#]/.test(destination.trim())) return '';

    try {
        const url = new URL(destination, 'https://www.acronis.com');
        if (!['http:', 'https:'].includes(url.protocol)) return '';
        const path = url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '');
        return path || '/';
    } catch {
        const path = destination.split(/[?#]/, 1)[0];
        return path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '';
    }
};

export const megaMenuLinkEvent = (options = {}) => {
    const { menuId, to, text, location, button = '', event = {} } = options;
    const incomingEvent = event || {};
    const eventButton = button === '' || button == null ? incomingEvent.button : button;

    return {
        ...incomingEvent,
        event: 'Acronis',
        category: 'Interactions',
        action: MEGA_MENU_ACTION_BY_ID[menuId] || `Menu ${labelFromId(menuId)}`,
        label: incomingEvent.label ?? megaMenuEventLabel(to),
        context: 'click',
        content: incomingEvent.content ?? text ?? '',
        location: location ?? incomingEvent.location ?? '',
        button: eventButton === '' || eventButton == null ? '' : String(eventButton),
    };
};
